import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Pencil, LogOut, Mail, CalendarCheck, CalendarDays, Upload, Loader2, Save, FileText, MailPlus, Wrench } from "lucide-react";
import { DEFAULT_CONTENT } from "@/lib/content";
import ThemeToggle from "@/components/ThemeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const emptyProduct = { title: "", category: "Keramik", description: "", price: "", image_url: "", image_pos: "50% 50%", available: true };
const emptyWorkshop = { title: "", date: "", date_iso: "", time_from: "", time_to: "", location: "", description: "", price: "", spots: 8, image_url: "", image_pos: "50% 50%" };
const emptyMarket = { title: "", date: "", date_iso: "", time_from: "", time_to: "", location: "", description: "", url: "" };

/** Parst ein "YYYY-MM-DD"-Datum als lokales Datum (kein UTC-Versatz). */
function parseIsoDate(iso) {
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

/** "10:00" -> "10", "09:30" -> "9:30" (deutsche Kurzschreibweise ohne führende Null / ":00"). */
function shortTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = String(parseInt(h, 10));
  return m === "00" ? hour : `${hour}:${m}`;
}

function buildDisplayDate(date, timeFrom, timeTo) {
  if (!date) return "";
  let s = format(date, "EEE d. MMMM yyyy", { locale: de });
  const tf = shortTime(timeFrom);
  const tt = shortTime(timeTo);
  if (tf && tt) s += `, ${tf}–${tt} Uhr`;
  else if (tf) s += `, ${tf} Uhr`;
  return s;
}

/**
 * Wiederverwendbares Datum/Zeit-Feld für Workshops & Märkte: Kalender-Popover (Tag) plus zwei
 * native Zeit-Inputs (von/bis). Schreibt bei jeder Änderung sowohl die strukturierten Felder
 * (date_iso, time_from, time_to) als auch einen automatisch erzeugten Anzeige-Text (form.date).
 */
function DateTimeField({ form, setForm }) {
  const [open, setOpen] = useState(false);
  const selected = parseIsoDate(form.date_iso);

  const pickDate = (date) => {
    if (!date) return;
    const iso = format(date, "yyyy-MM-dd");
    setForm((f) => ({ ...f, date_iso: iso, date: buildDisplayDate(date, f.time_from, f.time_to) }));
    setOpen(false);
  };

  const setTime = (key, value) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      const date = parseIsoDate(f.date_iso);
      if (date) next.date = buildDisplayDate(date, next.time_from, next.time_to);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs uppercase tracking-widest text-inkmuted">Datum</label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid="datetime-trigger"
                    className="mt-1.5 w-full flex items-center gap-2 rounded-md border border-linetone bg-panel2 px-3 py-2 text-left text-sm text-ink hover:bg-linetone/30 transition-colors">
              <CalendarDays size={15} className="text-inkmuted shrink-0" />
              <span className={form.date ? "" : "text-inkmuted"}>{form.date || "Datum wählen"}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-page border-linetone" align="start">
            <Calendar mode="single" selected={selected} onSelect={pickDate} locale={de} weekStartsOn={1} initialFocus />
          </PopoverContent>
        </Popover>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-inkmuted">Von</label>
          <input type="time" data-testid="datetime-from" value={form.time_from || ""}
                 onChange={(e) => setTime("time_from", e.target.value)}
                 className="mt-1.5 w-full rounded-md border border-linetone bg-panel2 px-3 py-2 text-sm text-ink" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-inkmuted">Bis (optional)</label>
          <input type="time" data-testid="datetime-to" value={form.time_to || ""}
                 onChange={(e) => setTime("time_to", e.target.value)}
                 className="mt-1.5 w-full rounded-md border border-linetone bg-panel2 px-3 py-2 text-sm text-ink" />
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-inkmuted">Anzeige-Text (automatisch erzeugt, anpassbar)</label>
        <Input value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })}
               className="mt-1.5 bg-panel2 border-linetone" data-testid="datetime-display" />
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-inkmuted">{label}</label>
      <Input {...props} className="mt-1.5 bg-panel2 border-linetone" />
    </div>
  );
}

function ItemDialog({ kind, initial, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isProduct = kind === "products";
  const isWorkshop = kind === "workshops";
  const isMarket = kind === "markets";
  const editing = !!initial.id;

  useEffect(() => { if (open) setForm(initial); }, [open, initial]);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: `${BACKEND_URL}${data.url}` }));
      toast.success("Bild hochgeladen.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.id;
      if (isWorkshop) payload.spots = Number(payload.spots) || 0;
      if (editing) await api.put(`/admin/${kind}/${initial.id}`, payload);
      else await api.post(`/admin/${kind}`, payload);
      toast.success(editing ? "Gespeichert." : "Hinzugefügt.");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-page border-linetone max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">
            {editing ? "Bearbeiten" : isProduct ? "Neues Werk" : isMarket ? "Neuer Markt" : "Neuer Workshop"}
          </DialogTitle>
          <DialogDescription className="text-inkmuted">Felder ausfüllen und speichern.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Titel" data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          {isProduct ? (
            <>
              <Field label="Kategorie" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <Field label="Preis" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              <div className="flex items-center gap-3">
                <Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="form-available" />
                <span className="text-sm text-inkmuted">Verfügbar</span>
              </div>
            </>
          ) : isMarket ? (
            <>
              <DateTimeField form={form} setForm={setForm} />
              <Field label="Ort" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              <Field label="Link (optional, z. B. Marktwebsite)" value={form.url || ""} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            </>
          ) : (
            <>
              <DateTimeField form={form} setForm={setForm} />
              <Field label="Ort" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Preis" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                <Field label="Plätze" type="number" value={form.spots} onChange={(e) => setForm({ ...form, spots: e.target.value })} />
              </div>
            </>
          )}
          {!isMarket && (
            <div>
              <label className="text-xs uppercase tracking-widest text-inkmuted">Bild</label>
              {form.image_url ? (
                <>
                  <div
                    className="relative mt-1.5 h-40 w-full overflow-hidden rounded-lg border border-linetone cursor-crosshair"
                    data-testid="image-focus-preview"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = Math.min(100, Math.max(0, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
                      const y = Math.min(100, Math.max(0, Math.round(((e.clientY - rect.top) / rect.height) * 100)));
                      setForm((f) => ({ ...f, image_pos: `${x}% ${y}%` }));
                    }}
                  >
                    <img src={form.image_url} alt="" className="h-full w-full object-cover"
                         style={{ objectPosition: form.image_pos || "50% 50%" }} />
                    <span
                      className="pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-sage shadow"
                      style={{
                        left: (form.image_pos || "50% 50%").split(" ")[0] || "50%",
                        top: (form.image_pos || "50% 50%").split(" ")[1] || "50%",
                      }}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <p className="text-xs text-inkmuted">Klicke ins Bild, um den sichtbaren Ausschnitt festzulegen.</p>
                    <button type="button" data-testid="image-pos-reset"
                            onClick={() => setForm((f) => ({ ...f, image_pos: "50% 50%" }))}
                            className="shrink-0 text-xs text-clay hover:underline underline-offset-2">
                      Zurücksetzen
                    </button>
                  </div>
                </>
              ) : (
                <div className="mt-1.5 h-16 w-16 rounded-lg bg-panel2 border border-linetone flex items-center justify-center text-inkmuted text-xs">kein Bild</div>
              )}
              <label className="mt-3 inline-flex items-center gap-2 cursor-pointer rounded-full border border-linetone bg-panel2 px-4 py-2 text-sm text-ink hover:bg-linetone/50 transition-colors" data-testid="upload-label">
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                {uploading ? "Lädt…" : "Bild hochladen"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="upload-input" disabled={uploading} />
              </label>
              <Input placeholder="oder Bild-URL einfügen" value={form.image_url}
                     onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                     className="mt-3 bg-panel2 border-linetone text-sm" data-testid="form-image-url" />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-widest text-inkmuted">Beschreibung</label>
            <Textarea rows={3} data-testid="form-description" value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="mt-1.5 bg-panel2 border-linetone resize-none" />
            <p className="mt-1 text-xs text-inkmuted">**fett** / *kursiv* möglich</p>
          </div>
          <Button onClick={save} disabled={loading} data-testid="form-save"
                  className="w-full rounded-full bg-sage text-sage-deep hover:bg-sage-dark py-6">
            {loading ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ItemList({ kind, items, reload }) {
  const isProduct = kind === "products";
  const del = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    await api.delete(`/admin/${kind}/${id}`);
    toast.success("Gelöscht.");
    reload();
  };
  return (
    <div className="space-y-3">
      {items.map((it) => (
        <div key={it.id} data-testid={`${kind}-row`} className="flex items-center gap-4 rounded-xl border border-linetone bg-panel p-3">
          {it.image_url && <img src={it.image_url} alt="" className="h-14 w-14 rounded-lg object-cover" />}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-ink truncate">{it.title}</p>
            <p className="text-sm text-inkmuted truncate">
              {isProduct ? `${it.category} · ${it.price}` : kind === "markets" ? `${it.date}${it.location ? " · " + it.location : ""}` : `${it.date} · ${it.price}`}
            </p>
          </div>
          <ItemDialog kind={kind} initial={it} onSaved={reload}
            trigger={<Button size="icon" variant="ghost" data-testid={`edit-${kind}`}><Pencil size={16} /></Button>} />
          <Button size="icon" variant="ghost" onClick={() => del(it.id)} data-testid={`delete-${kind}`}
                  className="text-clay hover:text-clay"><Trash2 size={16} /></Button>
        </div>
      ))}
      {items.length === 0 && <p className="text-inkmuted text-sm py-8 text-center">Noch keine Einträge.</p>}
    </div>
  );
}

const BOOKING_STATUS = {
  pending: { label: "Offen", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  confirmed: { label: "Bestätigt", cls: "bg-sage/20 text-sage-deep border-sage/40" },
  rejected: { label: "Abgelehnt", cls: "bg-clay/10 text-clay border-clay/25" },
};

function StatusBadge({ status }) {
  const s = BOOKING_STATUS[status] || BOOKING_STATUS.pending;
  return (
    <span data-testid="booking-status" className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

function BookingEditDialog({ booking, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", participants: 1, message: "", participantNamesText: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: booking.name || "",
        email: booking.email || "",
        participants: booking.participants || 1,
        message: booking.message || "",
        participantNamesText: (booking.participant_names || []).join(", "),
      });
    }
  }, [open, booking]);

  const save = async () => {
    setLoading(true);
    try {
      const participant_names = form.participantNamesText.split(",").map((n) => n.trim()).filter(Boolean);
      const { data } = await api.put(`/admin/bookings/${booking._id}`, {
        name: form.name,
        email: form.email,
        participants: Number(form.participants),
        message: form.message,
        participant_names,
      });
      toast.success("Buchung aktualisiert.");
      setOpen(false);
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" data-testid="booking-edit"><Pencil size={16} /></Button>
      </DialogTrigger>
      <DialogContent className="bg-page border-linetone max-w-lg" data-testid="booking-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">Buchung bearbeiten</DialogTitle>
          <DialogDescription className="text-inkmuted">Felder ausfüllen und speichern.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Name" data-testid="booking-edit-name" value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field label="E-Mail" type="email" data-testid="booking-edit-email" value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Field label="Personen" type="number" min={1} data-testid="booking-edit-participants" value={form.participants}
                 onChange={(e) => setForm({ ...form, participants: e.target.value })} />
          <div>
            <label className="text-xs uppercase tracking-widest text-inkmuted">Vornamen der Teilnehmer (durch Komma getrennt)</label>
            <Input data-testid="booking-edit-participant-names" value={form.participantNamesText}
                   placeholder="z. B. Anna, Ben"
                   onChange={(e) => setForm({ ...form, participantNamesText: e.target.value })}
                   className="mt-1.5 bg-panel2 border-linetone" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-inkmuted">Nachricht</label>
            <Textarea rows={3} data-testid="booking-edit-message" value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="mt-1.5 bg-panel2 border-linetone resize-none" />
          </div>
          <Button onClick={save} disabled={loading} data-testid="booking-edit-save"
                  className="w-full rounded-full bg-sage text-sage-deep hover:bg-sage-dark py-6">
            {loading ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MessageList({ items, kind, reload, onConfirm, onReject, onEdit }) {
  const isBookings = kind === "bookings";
  const del = async (id) => {
    await api.delete(`/admin/${kind}/${id}`);
    reload();
  };
  return (
    <div className="space-y-3">
      {items.map((m) => {
        const status = m.status || "pending";
        return (
          <div key={m._id} data-testid={`${kind}-message`} className="rounded-xl border border-linetone bg-panel p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-ink">{m.name} · <a href={`mailto:${m.email}`} className="text-sage-dark">{m.email}</a></p>
                  {isBookings && <StatusBadge status={status} />}
                </div>
                {m.workshop_title && <p className="text-sm text-clay mt-0.5">{m.workshop_title} · {m.participants} Pers.</p>}
                {isBookings && m.participant_names && m.participant_names.length > 0 && (
                  <p className="text-sm text-clay mt-0.5">Teilnehmer: {m.participant_names.join(", ")}</p>
                )}
                {m.product && <p className="text-sm text-clay mt-0.5">Interesse: {m.product}</p>}
                {m.message && <p className="text-sm text-inkmuted mt-2 leading-relaxed">{m.message}</p>}
                <p className="text-xs text-inkmuted/70 mt-2">{new Date(m.created_at).toLocaleString("de-DE")}</p>
                {isBookings && status === "pending" && (
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" onClick={() => onConfirm(m._id)} data-testid="booking-confirm"
                            className="rounded-full bg-sage text-sage-deep hover:bg-sage-dark">
                      Bestätigen
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onReject(m._id)} data-testid="booking-reject"
                            className="rounded-full border-linetone text-clay hover:text-clay">
                      Ablehnen
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {isBookings && <BookingEditDialog booking={m} onSaved={onEdit} />}
                <Button size="icon" variant="ghost" onClick={() => del(m._id)} className="text-clay"><Trash2 size={16} /></Button>
              </div>
            </div>
          </div>
        );
      })}
      {items.length === 0 && <p className="text-inkmuted text-sm py-8 text-center">Keine Nachrichten.</p>}
    </div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function CText({ label, value, onChange, area, rows = 3 }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-inkmuted">{label}</label>
      {area ? (
        <Textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)}
                  className="mt-1.5 bg-panel2 border-linetone resize-none" />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 bg-panel2 border-linetone" />
      )}
    </div>
  );
}

function CImage({ label, value, onChange }) {
  const [up, setUp] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUp(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${BACKEND}${data.url}`);
      toast.success("Bild hochgeladen.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setUp(false); }
  };
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-inkmuted">{label}</label>
      <div className="mt-1.5 flex items-center gap-4">
        {value ? <img src={value} alt="" className="h-20 w-20 rounded-lg object-cover border border-linetone" />
               : <div className="h-20 w-20 rounded-lg bg-panel2 border border-linetone" />}
        <label className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-linetone bg-panel2 px-4 py-2 text-sm text-ink hover:bg-linetone/50 transition-colors">
          {up ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}{up ? "Lädt…" : "Bild hochladen"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={up} />
        </label>
      </div>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="oder Bild-URL" className="mt-3 bg-panel2 border-linetone text-sm" />
    </div>
  );
}

function CImageList({ label, items, onChange }) {
  const [upIdx, setUpIdx] = useState(-1);
  const list = items || [];
  const setAt = (i, v) => onChange(list.map((x, idx) => (idx === i ? v : x)));
  const add = () => onChange([...list, ""]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const upload = async (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUpIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAt(i, `${BACKEND}${data.url}`);
      toast.success("Bild hochgeladen.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setUpIdx(-1); }
  };
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-inkmuted">{label}</label>
      <div className="mt-2 grid sm:grid-cols-2 gap-3">
        {list.map((src, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg bg-panel2 p-2">
            {src ? <img src={src} alt="" className="h-16 w-16 rounded object-cover border border-linetone" />
                 : <div className="h-16 w-16 rounded bg-panel border border-linetone" />}
            <div className="flex-1 min-w-0 space-y-2">
              <label className="inline-flex items-center gap-1.5 cursor-pointer rounded-full border border-linetone bg-panel px-3 py-1.5 text-xs text-ink hover:bg-linetone/40 transition-colors">
                {upIdx === i ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Hochladen
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(i, e)} disabled={upIdx === i} />
              </label>
              <Input value={src || ""} onChange={(e) => setAt(i, e.target.value)} placeholder="Bild-URL" className="bg-panel border-linetone text-xs h-8" />
            </div>
            <Button size="icon" variant="ghost" onClick={() => remove(i)} className="text-clay shrink-0"><Trash2 size={15} /></Button>
          </div>
        ))}
      </div>
      <Button onClick={add} variant="outline" className="mt-3 rounded-full border-linetone gap-2 text-sm"><Plus size={15} /> Bild hinzufügen</Button>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="rounded-xl border border-linetone bg-panel p-6">
      <h3 className="font-serif text-xl text-ink mb-5">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function InstagramConnection() {
  const [status, setStatus] = useState(null);
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/instagram").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!token.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/instagram", { token: token.trim() });
      toast.success(`Verbunden – ${data.item_count} Beitrag(e) geladen.`);
      setToken("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Instagram-Verbindung wirklich trennen?")) return;
    setDisconnecting(true);
    try {
      await api.delete("/admin/instagram");
      toast.success("Verbindung getrennt.");
      setToken("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setDisconnecting(false);
    }
  };

  const connected = !!status?.connected;
  const statusLine = connected
    ? `Verbunden – letzter Abruf: ${status.fetched_at ? new Date(status.fetched_at).toLocaleString("de-DE") : "noch nie"} · ${status.item_count} Beiträge`
    : "Nicht verbunden";

  return (
    <div className="rounded-lg border border-linetone bg-panel2 p-4 space-y-3">
      <h4 className="font-serif text-lg text-ink">Instagram-Verbindung (automatischer Feed)</h4>
      <p className="text-sm text-inkmuted" data-testid="ig-status">{statusLine}</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Input type="password" placeholder="Instagram-Zugriffstoken" value={token}
               onChange={(e) => setToken(e.target.value)}
               data-testid="ig-token-input" className="bg-panel border-linetone" />
        <div className="flex gap-2 shrink-0">
          <Button onClick={save} disabled={saving || !token.trim()} data-testid="ig-token-save"
                  className="rounded-full bg-sage text-sage-deep hover:bg-sage-dark whitespace-nowrap gap-2">
            {saving && <Loader2 size={15} className="animate-spin" />} Token speichern &amp; testen
          </Button>
          {connected && (
            <Button onClick={disconnect} disabled={disconnecting} variant="outline" data-testid="ig-disconnect"
                    className="rounded-full border-linetone text-clay hover:text-clay whitespace-nowrap">
              Verbindung trennen
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs text-inkmuted">
        Benötigt ein Instagram Business-/Creator-Konto und ein Zugriffstoken aus dem Meta-Entwicklerportal
        (siehe README). Der Feed aktualisiert sich danach automatisch alle 6 Stunden; das Token verlängert sich selbst.
      </p>
    </div>
  );
}

function ContentEditor() {
  const [c, setC] = useState(DEFAULT_CONTENT);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content").then((r) => {
      if (r.data && Object.keys(r.data).length) setC({ ...DEFAULT_CONTENT, ...r.data });
    }).catch(() => {});
  }, []);
  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const setChapter = (i, key, v) => setC((p) => {
    const ch = [...(p.chapters || [])]; ch[i] = { ...ch[i], [key]: v }; return { ...p, chapters: ch };
  });
  const setStat = (i, key, v) => setC((p) => {
    const st = [...(p.about_stats || [])]; st[i] = { ...st[i], [key]: v }; return { ...p, about_stats: st };
  });
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/content", c); toast.success("Inhalte gespeichert – Website aktualisiert."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between sticky top-[76px] z-10 bg-page/90 backdrop-blur py-3 -my-3">
        <p className="text-sm text-inkmuted">Alle Texte & Bilder der Startseite. Nach dem Speichern sofort live.</p>
        <Button onClick={save} disabled={saving} data-testid="content-save" className="rounded-full bg-sage text-sage-deep hover:bg-sage-dark gap-2">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern
        </Button>
      </div>
      <p className="text-xs text-inkmuted">Tipp: **fett**, *kursiv* sowie [zentriert]…[/zentriert] und [blocksatz]…[/blocksatz] funktionieren in allen Textfeldern.</p>

      <Group title="Hero (Startbereich)">
        <CText label="Kleine Überzeile" value={c.hero_overline} onChange={(v) => set("hero_overline", v)} />
        <div className="grid grid-cols-2 gap-4">
          <CText label="Titel Zeile 1" value={c.hero_line1} onChange={(v) => set("hero_line1", v)} />
          <CText label="Titel Zeile 3" value={c.hero_line3} onChange={(v) => set("hero_line3", v)} />
          <CText label="Zeile 2 – hervorgehoben" value={c.hero_line2_accent} onChange={(v) => set("hero_line2_accent", v)} />
          <CText label="Zeile 2 – Rest" value={c.hero_line2_suffix} onChange={(v) => set("hero_line2_suffix", v)} />
        </div>
        <CText label="Untertext" area value={c.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} />
        <CImage label="Hintergrundbild" value={c.hero_image} onChange={(v) => set("hero_image", v)} />
      </Group>

      <Group title="Laufband">
        <CText label="Begriffe (mit · getrennt)" area rows={2} value={c.marquee_items} onChange={(v) => set("marquee_items", v)} />
      </Group>

      <Group title="Über mich">
        <CText label="Überzeile" value={c.about_overline} onChange={(v) => set("about_overline", v)} />
        <div className="grid grid-cols-2 gap-4">
          <CText label="Überschrift" value={c.about_heading} onChange={(v) => set("about_heading", v)} />
          <CText label="Name (hervorgehoben)" value={c.about_name} onChange={(v) => set("about_name", v)} />
        </div>
        <CText label="Absatz 1" area value={c.about_p1} onChange={(v) => set("about_p1", v)} />
        <CText label="Absatz 2" area value={c.about_p2} onChange={(v) => set("about_p2", v)} />
        <CText label="Absatz 3" area value={c.about_p3} onChange={(v) => set("about_p3", v)} />
        <CImage label="Portraitbild" value={c.about_image} onChange={(v) => set("about_image", v)} />
        <div className="grid grid-cols-2 gap-4">
          <CText label="Badge Zahl" value={c.about_badge_number} onChange={(v) => set("about_badge_number", v)} />
          <CText label="Badge Text" value={c.about_badge_label} onChange={(v) => set("about_badge_label", v)} />
        </div>
        <p className="text-xs text-inkmuted">Beide Felder leer lassen, um die Badge komplett auszublenden.</p>
        <label className="text-xs uppercase tracking-widest text-inkmuted">Kennzahlen (3)</label>
        {(c.about_stats || []).map((s, i) => (
          <div key={i} className="grid grid-cols-2 gap-4">
            <Input value={s.value || ""} onChange={(e) => setStat(i, "value", e.target.value)} placeholder="Wert (z.B. 500+)" className="bg-panel2 border-linetone" />
            <Input value={s.label || ""} onChange={(e) => setStat(i, "label", e.target.value)} placeholder="Beschreibung" className="bg-panel2 border-linetone" />
          </div>
        ))}
      </Group>

      <Group title="Das Handwerk">
        <CText label="Überzeile" value={c.handwerk_overline} onChange={(v) => set("handwerk_overline", v)} />
        <CText label="Überschrift" area value={c.handwerk_heading} onChange={(v) => set("handwerk_heading", v)} />
        <label className="text-xs uppercase tracking-widest text-inkmuted">Kapitel (3)</label>
        {(c.chapters || []).map((ch, i) => (
          <div key={i} className="rounded-lg bg-panel2 p-4 space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <Input value={ch.n || ""} onChange={(e) => setChapter(i, "n", e.target.value)} placeholder="Nr." className="bg-panel border-linetone" />
              <Input value={ch.t || ""} onChange={(e) => setChapter(i, "t", e.target.value)} placeholder="Titel" className="col-span-3 bg-panel border-linetone" />
            </div>
            <Textarea rows={2} value={ch.d || ""} onChange={(e) => setChapter(i, "d", e.target.value)} placeholder="Beschreibung" className="bg-panel border-linetone resize-none" />
          </div>
        ))}
      </Group>

      <Group title="Galerie-Bereich (Überschrift)">
        <CText label="Überzeile" value={c.gallery_overline} onChange={(v) => set("gallery_overline", v)} />
        <div className="grid grid-cols-2 gap-4">
          <CText label="Überschrift Zeile 1" value={c.gallery_heading1} onChange={(v) => set("gallery_heading1", v)} />
          <CText label="Überschrift hervorgehoben" value={c.gallery_heading_accent} onChange={(v) => set("gallery_heading_accent", v)} />
        </div>
        <CText label="Einleitungstext" area value={c.gallery_intro} onChange={(v) => set("gallery_intro", v)} />
      </Group>

      <Group title="Impressionen (Bild-Galerie)">
        <CText label="Überzeile" value={c.showcase_overline} onChange={(v) => set("showcase_overline", v)} />
        <CText label="Überschrift" value={c.showcase_heading} onChange={(v) => set("showcase_heading", v)} />
        <CImageList label="Bilder (laufendes Band)" items={c.showcase_images} onChange={(v) => set("showcase_images", v)} />
      </Group>

      <Group title="Instagram">
        <div className="grid grid-cols-2 gap-4">
          <CText label="Überzeile / Handle" value={c.instagram_overline} onChange={(v) => set("instagram_overline", v)} />
          <CText label="Überschrift" value={c.instagram_heading} onChange={(v) => set("instagram_heading", v)} />
        </div>
        <CText label="Einleitungstext" area value={c.instagram_intro} onChange={(v) => set("instagram_intro", v)} />
        <CText label="Instagram-Link (URL)" value={c.instagram_url} onChange={(v) => set("instagram_url", v)} />
        <InstagramConnection />
        <CImageList label="Beitrags-Bilder" items={c.instagram_posts} onChange={(v) => set("instagram_posts", v)} />
        <p className="text-xs text-inkmuted">Wird nur angezeigt, solange keine Instagram-Verbindung aktiv ist.</p>
      </Group>

      <Group title="Workshops-Bereich (Überschrift)">
        <CText label="Überzeile" value={c.workshops_overline} onChange={(v) => set("workshops_overline", v)} />
        <CText label="Überschrift" area value={c.workshops_heading} onChange={(v) => set("workshops_heading", v)} />
        <CText label="Einleitungstext" area value={c.workshops_intro} onChange={(v) => set("workshops_intro", v)} />
        <CText label="AGB für Workshop-Buchungen (leer = kein AGB-Hinweis)" area rows={8} value={c.agb_text} onChange={(v) => set("agb_text", v)} />
        <p className="text-xs text-inkmuted">Die einzelnen Workshops verwaltest du im Tab „Workshops".</p>
      </Group>

      <Group title="Märkte">
        <CText label="Überzeile" value={c.maerkte_overline} onChange={(v) => set("maerkte_overline", v)} />
        <CText label="Überschrift" value={c.maerkte_heading} onChange={(v) => set("maerkte_heading", v)} />
        <CText label="Einleitungstext" area value={c.maerkte_intro} onChange={(v) => set("maerkte_intro", v)} />
        <p className="text-xs text-inkmuted">Die einzelnen Märkte verwaltest du im Tab „Märkte".</p>
      </Group>

      <Group title="Kontakt">
        <CText label="Überzeile" value={c.contact_overline} onChange={(v) => set("contact_overline", v)} />
        <div className="grid grid-cols-2 gap-4">
          <CText label="Überschrift" value={c.contact_heading} onChange={(v) => set("contact_heading", v)} />
          <CText label="Hervorgehoben" value={c.contact_accent} onChange={(v) => set("contact_accent", v)} />
        </div>
        <CText label="Einleitungstext" area value={c.contact_intro} onChange={(v) => set("contact_intro", v)} />
        <div className="grid grid-cols-3 gap-4">
          <CText label="E-Mail" value={c.contact_email} onChange={(v) => set("contact_email", v)} />
          <CText label="Ort" value={c.contact_location} onChange={(v) => set("contact_location", v)} />
          <CText label="Instagram" value={c.contact_instagram} onChange={(v) => set("contact_instagram", v)} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <CText label="TikTok (Anzeigename)" value={c.contact_tiktok} onChange={(v) => set("contact_tiktok", v)} />
          <CText label="TikTok-Link (URL)" value={c.tiktok_url} onChange={(v) => set("tiktok_url", v)} />
        </div>
        <p className="text-xs text-inkmuted">Leer lassen, um TikTok auszublenden.</p>
      </Group>

      <Group title="Footer">
        <CText label="Footer-Text" area value={c.footer_tagline} onChange={(v) => set("footer_tagline", v)} />
      </Group>

      <Group title="Rechtliches">
        <CText label="Impressum" area rows={10} value={c.impressum_text} onChange={(v) => set("impressum_text", v)} />
        <CText label="Datenschutzerklärung" area rows={10} value={c.datenschutz_text} onChange={(v) => set("datenschutz_text", v)} />
      </Group>

      <div className="flex justify-end pb-10">
        <Button onClick={save} disabled={saving} className="rounded-full bg-sage text-sage-deep hover:bg-sage-dark gap-2">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern
        </Button>
      </div>
    </div>
  );
}

const DEFAULT_ACCENT_COLOR = "#6E8163";

const EMAIL_TEMPLATE_META = [
  { key: "inquiry_receipt", title: "Eingangsbestätigung Kontaktanfrage", placeholders: "{name}, {nachricht}, {werk}" },
  { key: "booking_receipt", title: "Eingangsbestätigung Buchung", placeholders: "{name}, {workshop}, {personen}, {nachricht}" },
  { key: "booking_confirmed", title: "Buchungsbestätigung", placeholders: "{name}, {workshop}, {personen}" },
  { key: "booking_rejected", title: "Absage", placeholders: "{name}, {workshop}" },
];

const EMPTY_EMAIL_VALUES = {
  brand_title: "",
  accent_color: "",
  signature: "",
  inquiry_receipt: { subject: "", body: "" },
  booking_receipt: { subject: "", body: "" },
  booking_confirmed: { subject: "", body: "" },
  booking_rejected: { subject: "", body: "" },
};

function EmailTemplatePreviewDialog({ templateKey }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/email-templates/preview", { key: templateKey });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" data-testid={`email-preview-${templateKey}`}
                className="rounded-full border-linetone gap-2 text-sm">
          Vorschau
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-page border-linetone max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">Vorschau</DialogTitle>
          <DialogDescription className="text-inkmuted">Mit Beispieldaten, anhand der aktuell gespeicherten Vorlage.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-inkmuted py-8 text-center">Lädt…</p>
        ) : result ? (
          <div className="space-y-3">
            <p className="text-sm text-ink"><span className="text-inkmuted">Betreff: </span>{result.subject}</p>
            <iframe srcDoc={result.html} title="Vorschau" className="w-full h-[420px] bg-white rounded-lg border" sandbox="" />
          </div>
        ) : (
          <p className="text-sm text-inkmuted py-8 text-center">Keine Vorschau geladen.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EmailTemplatesEditor() {
  const [values, setValues] = useState(EMPTY_EMAIL_VALUES);
  const [defaults, setDefaults] = useState(EMPTY_EMAIL_VALUES);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/email-templates").then((r) => {
      setValues({ ...EMPTY_EMAIL_VALUES, ...r.data.values });
      setDefaults({ ...EMPTY_EMAIL_VALUES, ...r.data.defaults });
    }).catch(() => {});
  }, []);

  const setLayout = (key, v) => setValues((p) => ({ ...p, [key]: v }));
  const setTemplateField = (key, field, v) => setValues((p) => ({ ...p, [key]: { ...p[key], [field]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/email-templates", values);
      toast.success("E-Mail-Vorlagen gespeichert.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const accentPreview = /^#[0-9a-fA-F]{6}$/.test(values.accent_color || "") ? values.accent_color : DEFAULT_ACCENT_COLOR;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between sticky top-[76px] z-10 bg-page/90 backdrop-blur py-3 -my-3">
        <p className="text-sm text-inkmuted">Texte & Layout der Kunden-E-Mails. Leere Felder verwenden den Standardtext.</p>
        <Button onClick={save} disabled={saving} data-testid="email-save" className="rounded-full bg-sage text-sage-deep hover:bg-sage-dark gap-2">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern
        </Button>
      </div>

      <Group title="Absender & Layout">
        <CText label="Markenname" value={values.brand_title} onChange={(v) => setLayout("brand_title", v)} />
        <div>
          <label className="text-xs uppercase tracking-widest text-inkmuted">Akzentfarbe</label>
          <div className="mt-1.5 flex items-center gap-3">
            <input type="color" value={accentPreview} onChange={(e) => setLayout("accent_color", e.target.value)}
                   className="h-10 w-14 shrink-0 rounded border border-linetone bg-panel2" />
            <Input value={values.accent_color || ""} placeholder={DEFAULT_ACCENT_COLOR}
                   onChange={(e) => setLayout("accent_color", e.target.value)}
                   className="bg-panel2 border-linetone" />
          </div>
        </div>
        <CText label="Signatur" area rows={2} value={values.signature} onChange={(v) => setLayout("signature", v)} />
      </Group>

      {EMAIL_TEMPLATE_META.map(({ key, title, placeholders }) => (
        <Group key={key} title={title}>
          <div>
            <label className="text-xs uppercase tracking-widest text-inkmuted">Betreff</label>
            <Input value={values[key]?.subject || ""} placeholder={defaults[key]?.subject || ""}
                   onChange={(e) => setTemplateField(key, "subject", e.target.value)}
                   className="mt-1.5 bg-panel2 border-linetone" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-inkmuted">Text</label>
            <Textarea rows={6} value={values[key]?.body || ""} placeholder={defaults[key]?.body || ""}
                      onChange={(e) => setTemplateField(key, "body", e.target.value)}
                      className="mt-1.5 bg-panel2 border-linetone resize-none" />
          </div>
          <p className="text-xs text-inkmuted">Platzhalter: {placeholders}</p>
          <p className="text-xs text-inkmuted">**fett** und *kursiv* möglich. Leer = Standardtext.</p>
          <div className="flex justify-end">
            <EmailTemplatePreviewDialog templateKey={key} />
          </div>
        </Group>
      ))}
    </div>
  );
}


function MaintenanceEditor() {
  const [status, setStatus] = useState(null);
  const [password, setPassword] = useState("");
  const [toggling, setToggling] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/maintenance").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (checked) => {
    setToggling(true);
    try {
      const { data } = await api.put("/admin/maintenance", { active: checked });
      setStatus(data);
      toast.success(checked ? "Wartungsmodus aktiviert." : "Wartungsmodus deaktiviert.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setToggling(false);
    }
  };

  const savePassword = async () => {
    if (!password.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/maintenance", { password });
      setStatus(data);
      setPassword("");
      toast.success("Passwort gespeichert.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const active = !!status?.active;

  return (
    <div className="space-y-6">
      <Group title="Wartungsmodus">
        <div className="flex items-center gap-3">
          <Switch checked={active} onCheckedChange={toggle} disabled={toggling || !status} data-testid="maintenance-toggle" />
          <span className="text-sm text-inkmuted">
            {active
              ? "Wartungsmodus ist AKTIV – Besucher sehen den Sperrbildschirm."
              : "Website ist öffentlich erreichbar."}
          </span>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-inkmuted">Freischalt-Passwort</label>
          <div className="mt-1.5 flex flex-col sm:flex-row gap-3">
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   placeholder="Neues Passwort" data-testid="maintenance-password-input"
                   className="bg-panel2 border-linetone" />
            <Button onClick={savePassword} disabled={saving || !password.trim()} data-testid="maintenance-password-save"
                    className="rounded-full bg-sage text-sage-deep hover:bg-sage-dark whitespace-nowrap gap-2">
              {saving && <Loader2 size={15} className="animate-spin" />} Passwort speichern
            </Button>
          </div>
          <p className="mt-2 text-xs text-inkmuted">
            Mit diesem Passwort können Besucher die Seite trotz Wartungsmodus ansehen. Muss gesetzt sein,
            bevor der Wartungsmodus aktiviert werden kann.
          </p>
        </div>
      </Group>
    </div>
  );
}


export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/workshops").then((r) => setWorkshops(r.data)).catch(() => {});
    api.get("/markets").then((r) => setMarkets(r.data)).catch(() => {});
    api.get("/admin/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const signOut = () => { logout(); nav("/admin/login"); };

  const confirmBooking = async (id) => {
    try {
      const { data } = await api.post(`/admin/bookings/${id}/confirm`);
      setBookings((bs) => bs.map((b) => (b._id === id ? data : b)));
      toast.success("Buchung bestätigt.");
      api.get("/workshops").then((r) => setWorkshops(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const rejectBooking = async (id) => {
    try {
      const { data } = await api.post(`/admin/bookings/${id}/reject`);
      setBookings((bs) => bs.map((b) => (b._id === id ? data : b)));
      toast.success("Buchung abgelehnt.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const editBooking = (updated) => {
    setBookings((bs) => bs.map((b) => (b._id === updated._id ? updated : b)));
    api.get("/workshops").then((r) => setWorkshops(r.data)).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-page">
      <header className="border-b border-linetone bg-panel/70 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.webp" alt="Tonpoesie" className="h-14 w-14 rounded-full object-cover" />
            <div>
              <p className="font-serif text-2xl text-ink">Tonpoesie Studio</p>
              <p className="text-xs text-inkmuted">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="text-sm text-inkmuted hover:text-ink transition-colors px-3">Website</a>
            <ThemeToggle className="p-2 text-inkmuted hover:text-ink" />
            <Button variant="outline" onClick={signOut} data-testid="logout-btn" className="rounded-full border-linetone gap-2">
              <LogOut size={15} /> Abmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <Tabs defaultValue="products">
          <TabsList className="bg-panel2 flex-wrap h-auto">
            <TabsTrigger value="content" data-testid="tab-content"><FileText size={14} className="mr-1.5" /> Inhalte</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Werke ({products.length})</TabsTrigger>
            <TabsTrigger value="workshops" data-testid="tab-workshops">Workshops ({workshops.length})</TabsTrigger>
            <TabsTrigger value="markets" data-testid="tab-markets">Märkte ({markets.length})</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries"><Mail size={14} className="mr-1.5" /> Anfragen ({inquiries.length})</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings"><CalendarCheck size={14} className="mr-1.5" /> Buchungen ({bookings.length})</TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails"><MailPlus size={14} className="mr-1.5" /> E-Mails</TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="tab-maintenance"><Wrench size={14} className="mr-1.5" /> Wartung</TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="mt-6">
            <ContentEditor />
          </TabsContent>

          <TabsContent value="products" className="mt-6">
            <div className="flex justify-end mb-4">
              <ItemDialog kind="products" initial={emptyProduct} onSaved={load}
                trigger={<Button data-testid="add-product" className="rounded-full bg-sage text-sage-deep hover:bg-sage-dark gap-2"><Plus size={16} /> Werk</Button>} />
            </div>
            <ItemList kind="products" items={products} reload={load} />
          </TabsContent>

          <TabsContent value="workshops" className="mt-6">
            <div className="flex justify-end mb-4">
              <ItemDialog kind="workshops" initial={emptyWorkshop} onSaved={load}
                trigger={<Button data-testid="add-workshop" className="rounded-full bg-sage text-sage-deep hover:bg-sage-dark gap-2"><Plus size={16} /> Workshop</Button>} />
            </div>
            <ItemList kind="workshops" items={workshops} reload={load} />
          </TabsContent>

          <TabsContent value="markets" className="mt-6">
            <div className="flex justify-end mb-4">
              <ItemDialog kind="markets" initial={emptyMarket} onSaved={load}
                trigger={<Button data-testid="add-market" className="rounded-full bg-sage text-sage-deep hover:bg-sage-dark gap-2"><Plus size={16} /> Markt</Button>} />
            </div>
            <ItemList kind="markets" items={markets} reload={load} />
          </TabsContent>

          <TabsContent value="inquiries" className="mt-6">
            <MessageList items={inquiries} kind="inquiries" reload={load} />
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <MessageList items={bookings} kind="bookings" reload={load} onConfirm={confirmBooking} onReject={rejectBooking} onEdit={editBooking} />
          </TabsContent>

          <TabsContent value="emails" className="mt-6">
            <EmailTemplatesEditor />
          </TabsContent>

          <TabsContent value="maintenance" className="mt-6">
            <MaintenanceEditor />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
